import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Monitor, Tv, Smartphone, Tablet, Play, Eye, MousePointer, Clock, Users, ChevronDown, ChevronUp, Wifi, MapPin, CheckCircle2, XCircle, X, Trophy, ZoomIn, ZoomOut, Maximize2, Minimize2, TrendingUp, TrendingDown, AlertTriangle, Send, FastForward, Rewind, Gauge,
} from 'lucide-react';
import { API } from './shared';
import { LoadingSpinner } from './core';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import worldData from '../../data/world-110m.json';

const cardCls = (light) => `rounded-xl border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`;
const txtMain = (light) => (light ? 'text-gray-900' : 'text-white');
const txtSub = (light) => (light ? 'text-gray-500' : 'text-white/50');

const KIND_ICON = { web: Monitor, tv: Tv, mobile: Smartphone, tablet: Tablet };
const KIND_LABEL = { web: 'Web', tv: 'TV', mobile: 'Mobile', tablet: 'Tablette' };

const fmtDur = (s) => {
  if (!s || s < 0) return '0 min';
  if (s < 60) return `${Math.round(s)} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min`;
};
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const since = (iso) => { try { return fmtDur((Date.now() - new Date(iso).getTime()) / 1000); } catch { return ''; } };

function DeviceChip({ kind, device, light }) {
  const Icon = KIND_ICON[kind] || Monitor;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold ${light ? 'bg-gray-100 text-gray-600' : 'bg-white/8 text-white/70'}`}>
      <Icon size={12} className="text-[#E9641A]" />{device || KIND_LABEL[kind] || 'Web'}
    </span>
  );
}

/* ─── FICHE UTILISATEUR ─── */
export function UserActivityModal({ userId, light, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('history');

  useEffect(() => {
    setData(null);
    axios.get(`${API}/admin/activity/user/${userId}`, { withCredentials: true }).then(r => setData(r.data)).catch(() => onClose());
  }, [userId, onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4" onClick={onClose} data-testid="user-activity-modal">
      <div className={`${cardCls(light)} w-full max-w-2xl max-h-[88vh] overflow-y-auto p-5`} onClick={e => e.stopPropagation()}>
        {!data ? <LoadingSpinner /> : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className={`text-base font-bold ${txtMain(light)}`}>{data.user.name || data.user.email}</p>
                <p className={`text-xs ${txtSub(light)}`}>{data.user.email} · forfait {data.user.plan || '—'} ({data.user.status || '—'}) · {data.user.profiles} profil{data.user.profiles > 1 ? 's' : ''} · inscrit le {fmtDT(data.user.created_at)}</p>
              </div>
              <button onClick={onClose} data-testid="user-modal-close" className={`p-1.5 ${txtSub(light)} hover:text-red-400`}><X size={16} /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[[data.stats.sessions, 'Sessions (30 j)'], [fmtDur(data.stats.total_duration_s), 'Temps de connexion'], [fmtDur(data.stats.total_watch_s), 'Temps de visionnage']].map(([v, l], i) => (
                <div key={i} className={`rounded-lg p-3 text-center ${light ? 'bg-gray-50' : 'bg-white/3'}`}>
                  <p className={`text-lg font-bold ${txtMain(light)}`}>{v}</p>
                  <p className={`text-[10px] uppercase ${txtSub(light)}`}>{l}</p>
                </div>
              ))}
            </div>
            <div className={`flex rounded-lg p-0.5 mb-4 w-fit ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
              {[['history', 'Contenus regardés'], ['sessions', 'Sessions détaillées'], ['player', 'Comportement lecteur']].map(([id, lbl]) => (
                <button key={id} data-testid={`user-tab-${id}`} onClick={() => setTab(id)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-md ${tab === id ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500' : 'text-white/40'}`}>{lbl}</button>
              ))}
            </div>
            {tab === 'history' && (
              <div className="space-y-2" data-testid="user-watch-history">
                {data.watch_history.length === 0 && <p className={`text-xs py-6 text-center ${txtSub(light)}`}>Aucun visionnage enregistré</p>}
                {data.watch_history.map((h, i) => (
                  <div key={i} className={`flex items-center gap-3 p-2 rounded-lg ${light ? 'bg-gray-50' : 'bg-white/3'}`}>
                    {h.thumbnail_url ? <img src={h.thumbnail_url} alt="" className="w-16 h-9 rounded object-cover flex-none" /> : <span className={`w-16 h-9 rounded flex-none ${light ? 'bg-gray-200' : 'bg-white/10'}`} />}
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-bold truncate ${txtMain(light)}`}>{h.title}</p>
                      <p className={`text-[11px] ${txtSub(light)}`}>{h.profile_name && `${h.profile_name} · `}{fmtDT(h.updated_at)}</p>
                    </div>
                    <div className="w-24 flex-none">
                      <div className={`h-1.5 rounded-full ${light ? 'bg-gray-200' : 'bg-white/10'}`}>
                        <div className="h-full bg-[#E9641A] rounded-full" style={{ width: `${Math.min(100, h.progress || 0)}%` }} />
                      </div>
                      <p className={`text-[10px] text-right mt-0.5 ${txtSub(light)}`}>{h.progress >= 95 ? 'Terminé' : `${h.progress || 0}%`}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {tab === 'sessions' && (
              <div className="space-y-2" data-testid="user-sessions">
                {data.sessions.length === 0 && <p className={`text-xs py-6 text-center ${txtSub(light)}`}>Aucune session (le suivi a démarré récemment)</p>}
                {data.sessions.map((s, i) => (
                  <div key={i} className={`p-2.5 rounded-lg ${light ? 'bg-gray-50' : 'bg-white/3'}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <DeviceChip kind={s.kind} device={s.device} light={light} />
                      <span className={`text-[11px] ${txtSub(light)}`}>{fmtDT(s.started_at)} · {fmtDur(s.duration_s)}{(s.watch_seconds || 0) > 0 ? ` · ${fmtDur(s.watch_seconds)} regardées` : ''}</span>
                    </div>
                    {(s.watched || []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {s.watched.map((t, j) => <span key={j} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#E9641A]/10 text-[#E9641A]">{t}</span>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {tab === 'player' && <PlayerBehavior userId={userId} light={light} />}
          </>
        )}
      </div>
    </div>
  );
}

/* ─── COMPORTEMENT LECTEUR ─── */
const fmtPos = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return (h > 0 ? `${h}:` : '') + `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};
const PLAYER_EV = {
  start: ['Lecture démarrée', Play, 'text-[#E9641A]'],
  play: ['Reprise', Play, 'text-emerald-500'],
  pause: ['Pause', Clock, 'text-amber-500'],
  fwd: ['Avance rapide', FastForward, 'text-sky-400'],
  back: ['Retour arrière', Rewind, 'text-sky-400'],
  seek: ['Saut dans la timeline', MousePointer, 'text-purple-400'],
  rate: ['Vitesse de lecture', Gauge, 'text-pink-400'],
  ad_view: ['Publicité vue', Eye, 'text-teal-400'],
  ad_click: ['Clic sur la pub', MousePointer, 'text-teal-400'],
  ad_end: ['Publicité terminée', CheckCircle2, 'text-teal-400'],
  ended: ['Contenu terminé', CheckCircle2, 'text-emerald-500'],
  filter_on: ['Filtre activé', Eye, 'text-indigo-400'],
  filter_off: ['Filtre désactivé', XCircle, 'text-indigo-400'],
};

function evDetail(e) {
  const m = e.meta || {};
  switch (e.type) {
    case 'pause': return `à ${fmtPos(e.at)}`;
    case 'play': return m.paused_s ? `à ${fmtPos(e.at)} — après ${fmtDur(m.paused_s)} de pause` : `à ${fmtPos(e.at)}`;
    case 'fwd': return `+${m.sec || 10}s depuis ${fmtPos(e.at)}`;
    case 'back': return `-${m.sec || 10}s depuis ${fmtPos(e.at)}`;
    case 'seek': return `de ${fmtPos(e.at)} → ${fmtPos(m.to)}`;
    case 'rate': return `x${m.rate} à ${fmtPos(e.at)}`;
    case 'ad_view': return `${m.ad || m.ad_id || ''} (${m.slot === 'pre-roll' ? 'début du film' : 'milieu du film'})`;
    case 'ad_click': return `${m.ad || m.ad_id || ''}`;
    case 'ad_end': return `${m.ad || m.ad_id || ''}`;
    case 'ended': return `à ${fmtPos(e.at)}`;
    case 'filter_on': return `« ${m.filter} » à ${fmtPos(e.at)}`;
    case 'filter_off': return `« ${m.filter} » à ${fmtPos(e.at)}`;
    default: return `à ${fmtPos(e.at)}`;
  }
}

export function PlayerBehavior({ userId, light }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    const load = () => axios.get(`${API}/admin/telemetry/user/${userId}`, { withCredentials: true }).then(r => setData(r.data)).catch(() => setData({ live: [], sessions: [] }));
    load();
    const iv = setInterval(load, 10000);
    const tick = setInterval(() => setTick(t => t + 1), 1000);
    return () => { clearInterval(iv); clearInterval(tick); };
  }, [userId]);

  if (!data) return <LoadingSpinner />;
  const chip = (label, cls) => <span key={label} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
  return (
    <div data-testid="user-player-behavior">
      {data.live.map((l, i) => {
        const elapsed = l.paused ? 0 : (Date.now() - new Date(l.last_seen).getTime()) / 1000 * (l.rate || 1);
        const pos = (l.position || 0) + elapsed;
        return (
          <div key={i} className={`mb-3 p-3 rounded-xl border ${light ? 'bg-emerald-50 border-emerald-200' : 'bg-emerald-500/8 border-emerald-500/25'}`} data-testid={`player-live-${i}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="relative flex w-2.5 h-2.5"><span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${l.paused ? 'bg-amber-400' : 'bg-emerald-400'}`} /><span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${l.paused ? 'bg-amber-500' : 'bg-emerald-500'}`} /></span>
              <p className={`text-xs font-bold ${txtMain(light)}`}>EN DIRECT · {l.paused ? 'En pause' : 'En lecture'}{(l.rate || 1) !== 1 ? ` x${l.rate}` : ''}</p>
            </div>
            <p className={`text-xs mt-1.5 ${txtMain(light)}`}>
              <span className="text-[#E9641A] font-bold">{l.content_title || 'Contenu'}</span>
              {' '}— position actuelle : <span className="font-mono font-bold">{fmtPos(pos)}</span>
            </p>
            <p className={`text-[10px] mt-0.5 ${txtSub(light)}`}>{l.device}{l.profile_name ? ` · profil ${l.profile_name}` : ''}</p>
            <p className={`text-[10px] mt-0.5 font-semibold ${(l.filters || []).length ? 'text-indigo-400' : txtSub(light)}`}>
              {(l.filters || []).length ? `Filtres actifs : ${l.filters.join(', ')}` : 'Regarde sans filtre'}
            </p>
          </div>
        );
      })}
      {data.live.length === 0 && <p className={`text-[11px] mb-3 ${txtSub(light)}`}>Aucune lecture en cours en ce moment.</p>}
      {data.sessions.length === 0 && <p className={`text-xs py-6 text-center ${txtSub(light)}`}>Aucun événement lecteur enregistré (le suivi démarre avec les nouvelles lectures).</p>}
      <div className="space-y-2">
        {data.sessions.map((s) => {
          const a = s.agg;
          const isOpen = open === s.session_id;
          return (
            <div key={s.session_id} className={`rounded-lg overflow-hidden ${light ? 'bg-gray-50' : 'bg-white/3'}`} data-testid={`player-session-${s.session_id}`}>
              <button onClick={() => setOpen(isOpen ? null : s.session_id)} className="w-full text-left p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-xs font-bold ${txtMain(light)}`}>{fmtDT(s.started_at)} · {s.contents.join(', ') || 'Lecture'}</p>
                  {isOpen ? <ChevronUp size={14} className={txtSub(light)} /> : <ChevronDown size={14} className={txtSub(light)} />}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {a.pauses === 0 && a.ended > 0 && chip('Regardé sans pause', 'bg-emerald-500/15 text-emerald-500')}
                  {a.pauses > 0 && chip(`${a.pauses} pause${a.pauses > 1 ? 's' : ''}${a.pause_total_s ? ` (${fmtDur(a.pause_total_s)})` : ''}`, 'bg-amber-500/15 text-amber-500')}
                  {a.fwd > 0 && chip(`Avance ×${a.fwd}`, 'bg-sky-500/15 text-sky-400')}
                  {a.back > 0 && chip(`Retour ×${a.back}`, 'bg-sky-500/15 text-sky-400')}
                  {a.seeks > 0 && chip(`Sauts timeline ×${a.seeks}`, 'bg-purple-500/15 text-purple-400')}
                  {(a.rates || []).length > 0 && chip(`Vitesses : ${a.rates.map(r => `x${r}`).join(' ')}`, 'bg-pink-500/15 text-pink-400')}
                  {a.ad_clicks > 0 && chip(`Clics pub ×${a.ad_clicks}`, 'bg-teal-500/15 text-teal-400')}
                  {(a.filters_used || []).length > 0 && chip(`Filtres : ${a.filters_used.join(', ')}`, 'bg-indigo-500/15 text-indigo-400')}
                  {(a.filter_toggles || 0) > 0 && chip(`Filtres modifiés ×${a.filter_toggles}`, 'bg-indigo-500/15 text-indigo-400')}
                  {(a.filters_used || []).length === 0 && !a.filter_toggles && chip('Sans filtre', light ? 'bg-gray-100 text-gray-400' : 'bg-white/8 text-white/35')}
                  {a.ended > 0 && chip('Terminé', 'bg-emerald-500/15 text-emerald-500')}
                </div>
              </button>
              {isOpen && (
                <div className={`px-3 pb-3 border-t ${light ? 'border-gray-100' : 'border-white/5'}`}>
                  <div className="mt-2 space-y-0 max-h-72 overflow-y-auto">
                    {s.events.map((e, j) => {
                      const [lbl, Icon, cls] = PLAYER_EV[e.type] || ['?', MousePointer, txtSub(light)];
                      return (
                        <div key={j} className="flex items-start gap-2 relative pb-2">
                          {j < s.events.length - 1 && <span className={`absolute left-[8px] top-4 bottom-0 w-px ${light ? 'bg-gray-200' : 'bg-white/10'}`} />}
                          <span className={`w-[17px] h-[17px] rounded-full flex items-center justify-center flex-none mt-0.5 ${light ? 'bg-gray-100' : 'bg-white/8'}`}>
                            <Icon size={9} className={cls} />
                          </span>
                          <p className={`text-[11px] leading-4 ${txtSub(light)}`}>
                            <span className={`font-mono ${light ? 'text-gray-400' : 'text-white/30'}`}>{fmtTime(e.ts)}</span>
                            {' '}<span className={`font-semibold ${txtMain(light)}`}>{lbl}</span>
                            {' '}— {evDetail(e)}
                            {e.content_title && s.contents.length > 1 ? <span className="text-[#E9641A]"> · {e.content_title}</span> : null}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MapCanvas({ pts, zoom, center, onMove, onZoomBtn, mapKey, light, tall }) {
  return (
    <div className="relative">
      <ComposableMap projection="geoEqualEarth" projectionConfig={{ scale: 155 }}
        style={{ width: '100%', height: 'auto', maxHeight: tall ? '80vh' : 360 }}>
        <ZoomableGroup key={mapKey} zoom={zoom} center={center} minZoom={1} maxZoom={16} onMoveEnd={onMove}>
          <Geographies geography={worldData}>
            {({ geographies }) => geographies.map(geo => (
              <Geography key={geo.rsmKey} geography={geo}
                fill={light ? '#e5e7eb' : '#241640'} stroke={light ? '#cbd5e1' : '#33215c'} strokeWidth={0.5 / zoom}
                style={{ default: { outline: 'none' }, hover: { outline: 'none' }, pressed: { outline: 'none' } }} />
            ))}
          </Geographies>
          {pts.map((p, i) => (
            <Marker key={p.k} coordinates={[p.lon + ((i * 37) % 100) / 100 * 1.4 - 0.7, p.lat + ((i * 53) % 100) / 100 * 1.0 - 0.5]}>
              <circle r={(p.type === 'watching' ? 5 : 4) / Math.sqrt(zoom)} fill={p.type === 'watching' ? '#E9641A' : '#22c55e'}
                stroke={light ? '#fff' : '#0d0d14'} strokeWidth={1.4 / Math.sqrt(zoom)} opacity={0.95}>
                <title>{p.label}</title>
              </circle>
            </Marker>
          ))}
        </ZoomableGroup>
      </ComposableMap>
      <div className="absolute bottom-3 right-3 flex flex-col gap-1.5">
        <button data-testid="map-zoom-in" onClick={() => onZoomBtn(1.6)}
          className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-colors ${light ? 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100' : 'bg-[#1a0e2e] border-white/15 text-white hover:bg-white/10'}`}>
          <ZoomIn size={15} />
        </button>
        <button data-testid="map-zoom-out" onClick={() => onZoomBtn(1 / 1.6)}
          className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-colors ${light ? 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100' : 'bg-[#1a0e2e] border-white/15 text-white hover:bg-white/10'}`}>
          <ZoomOut size={15} />
        </button>
      </div>
      {pts.length === 0 && (
        <p className={`absolute inset-0 flex items-center justify-center text-xs pointer-events-none ${light ? 'text-gray-500' : 'text-white/50'}`} data-testid="live-map-empty">
          Aucune position disponible pour l'instant — les points apparaissent dès qu'un utilisateur se connecte.
        </p>
      )}
    </div>
  );
}

/* ─── CARTE DES SPECTATEURS ─── */
export function LiveMap({ online, watching, light }) {
  const pts = [];
  watching.forEach((w, i) => {
    if (w.lat != null && w.lon != null) pts.push({ lat: w.lat, lon: w.lon, label: `▶ ${w.user_name || w.profile_name || ''} — ${w.content_title || ''} (${w.city || ''})`, type: 'watching', k: `w${i}` });
  });
  online.forEach((s, i) => {
    if (s.lat != null && s.lon != null) pts.push({ lat: s.lat, lon: s.lon, label: `${s.user_name || s.user_email || ''} — ${s.city || ''}`, type: 'online', k: `o${i}` });
  });
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState([10, 25]);
  const [expanded, setExpanded] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const onMove = ({ coordinates, zoom: z }) => { setCenter(coordinates); setZoom(z); };
  const onZoomBtn = (f) => { setZoom(z => Math.max(1, Math.min(16, z * f))); setMapKey(k => k + 1); };
  const canvasProps = { pts, zoom, center, onMove, onZoomBtn, mapKey, light };

  const header = (
    <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
      <h3 className={`text-sm font-semibold flex items-center gap-2 ${txtMain(light)}`}><MapPin size={15} className="text-[#E9641A]" /> Carte des spectateurs</h3>
      <div className={`flex items-center gap-3 text-[11px] ${txtSub(light)}`}>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> Connecté ({online.filter(s => s.lat != null).length})</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#E9641A] inline-block" /> En lecture ({watching.filter(w => w.lat != null).length})</span>
        <button data-testid={expanded ? 'map-collapse' : 'map-expand'} onClick={() => setExpanded(e => !e)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-bold border transition-colors ${light ? 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100' : 'bg-white/5 border-white/15 text-white hover:bg-white/10'}`}>
          {expanded ? <><Minimize2 size={13} /> Réduire</> : <><Maximize2 size={13} /> Agrandir</>}
        </button>
      </div>
    </div>
  );

  if (expanded) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="live-map-fullscreen" onClick={() => setExpanded(false)}>
        <div className={`${cardCls(light)} w-full max-w-6xl max-h-[92vh] overflow-hidden p-4`} onClick={e => e.stopPropagation()}>
          {header}
          <MapCanvas {...canvasProps} tall />
        </div>
      </div>
    );
  }
  return (
    <div className={`${cardCls(light)} p-4 mb-5`} data-testid="live-map">
      {header}
      <MapCanvas {...canvasProps} />
    </div>
  );
}

/* ─── TOP DES VILLES ─── */
function TopCities({ cities, light }) {
  if (!cities || cities.length === 0) return null;
  const max = Math.max(1, ...cities.map(c => c.sessions));
  return (
    <div className={`${cardCls(light)} p-4 mb-5`} data-testid="top-cities">
      <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${txtMain(light)}`}>
        <MapPin size={15} className="text-[#E9641A]" /> Top des villes les plus actives <span className={`text-[10px] font-normal ${txtSub(light)}`}>(30 derniers jours)</span>
      </h3>
      <div className="space-y-2">
        {cities.map((c, i) => (
          <div key={c.city} className="flex items-center gap-3" data-testid={`top-city-${i}`}>
            <span className={`w-6 text-center text-xs font-bold ${i === 0 ? 'text-[#E9641A]' : txtSub(light)}`}>{i + 1}</span>
            <span className={`text-xs font-semibold w-44 truncate ${txtMain(light)}`}>{c.city}</span>
            <div className={`flex-1 h-2 rounded-full ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
              <div className={`h-full rounded-full ${i === 0 ? 'bg-[#E9641A]' : 'bg-[#E9641A]/50'}`} style={{ width: `${(c.sessions / max) * 100}%` }} />
            </div>
            <span className={`text-[11px] w-40 text-right whitespace-nowrap ${txtSub(light)}`}>
              {c.sessions} session{c.sessions > 1 ? 's' : ''} · {c.users} utilisateur{c.users > 1 ? 's' : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── EN DIRECT ─── */
export function ActivityLive({ theme }) {
  const light = theme === 'light';
  const [data, setData] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const load = () => axios.get(`${API}/admin/activity/live`, { withCredentials: true }).then(r => setData(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, []);

  if (!data) return <LoadingSpinner />;
  return (
    <div data-testid="activity-live">
      <LiveMap online={data.online} watching={data.watching} light={light} />
      <TopCities cities={data.top_cities} light={light} />
      {(data.record?.max_concurrent || 0) > 0 && (
        <div className={`flex items-center gap-2.5 mb-4 px-4 py-2.5 rounded-xl border ${light ? 'bg-amber-50 border-amber-200' : 'bg-amber-500/8 border-amber-500/20'}`} data-testid="audience-record-banner">
          <Trophy size={15} className="text-amber-500 flex-none" />
          <p className={`text-xs ${light ? 'text-amber-800' : 'text-amber-300'}`}>
            <b>Record : {data.record.max_concurrent} connexion{data.record.max_concurrent > 1 ? 's' : ''} simultanée{data.record.max_concurrent > 1 ? 's' : ''}</b>
            {data.record.reached_at && <> — atteint le {fmtDT(data.record.reached_at)}</>} · une notification est envoyée à chaque nouveau record 🔔
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <div className={`${cardCls(light)} p-5`}>
        <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${txtMain(light)}`}>
          <span className="relative flex w-2.5 h-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" /></span>
          Connectés maintenant <span className="text-[#E9641A]">({data.online.length})</span>
        </h3>
        {data.online.length === 0 && <p className={`text-xs py-6 text-center ${txtSub(light)}`}>Personne n'est connecté en ce moment</p>}
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {data.online.map((s, i) => (
            <div key={s.session_id || i} className={`flex items-center gap-3 p-2.5 rounded-lg ${light ? 'bg-gray-50' : 'bg-white/3'}`} data-testid={`live-online-${i}`}>
              <Wifi size={14} className="text-green-500 flex-none" />
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-bold truncate ${txtMain(light)}`}>
                  <button onClick={() => s.user_id && setUserId(s.user_id)} data-testid={`live-user-btn-${i}`} className="hover:text-[#E9641A] hover:underline transition-colors">{s.user_name || s.user_email}</button>
                  {s.profile_name ? ` · ${s.profile_name}` : ''}
                </p>
                <p className={`text-[11px] truncate ${txtSub(light)}`}>{s.user_email} · sur « {s.last_page === '/' ? 'Accueil' : s.last_page} » · depuis {since(s.started_at)}</p>
              </div>
              <DeviceChip kind={s.kind} device={s.device} light={light} />
            </div>
          ))}
        </div>
      </div>
      <div className={`${cardCls(light)} p-5`}>
        <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${txtMain(light)}`}>
          <Play size={15} className="text-[#E9641A]" /> En train de regarder <span className="text-[#E9641A]">({data.watching.length})</span>
        </h3>
        {data.watching.length === 0 && <p className={`text-xs py-6 text-center ${txtSub(light)}`}>Aucune lecture en cours</p>}
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {data.watching.map((w, i) => (
            <div key={w.session_id || i} className={`flex items-center gap-3 p-2.5 rounded-lg ${light ? 'bg-gray-50' : 'bg-white/3'}`} data-testid={`live-watching-${i}`}>
              <Play size={14} className="text-[#E9641A] flex-none" />
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-bold truncate ${txtMain(light)}`}>{w.content_title || 'Contenu inconnu'}</p>
                <p className={`text-[11px] truncate ${txtSub(light)}`}>
                  {(w.user_name || w.user_email) && <>{w.user_name || w.user_email} · </>}{w.profile_name} · <MapPin size={9} className="inline -mt-0.5" /> {w.city || '—'} · depuis {since(w.started_at)}
                </p>
              </div>
              <span className={`text-[11px] font-semibold px-2 py-1 rounded-md ${light ? 'bg-gray-100 text-gray-600' : 'bg-white/8 text-white/70'}`}>{w.device}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
      {userId && <UserActivityModal userId={userId} light={light} onClose={() => setUserId(null)} />}
    </div>
  );
}

/* ─── SESSIONS ─── */
const EV_ICON = { nav: MousePointer, content_view: Eye, play: Play };
const EV_LABEL = { nav: 'Navigation', content_view: 'Fiche consultée', play: 'Lecture lancée' };

export function ActivitySessions({ theme }) {
  const light = theme === 'light';
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [limit, setLimit] = useState(40);
  const [userId, setUserId] = useState(null);

  const load = useCallback((lim) => {
    axios.get(`${API}/admin/activity/sessions?limit=${lim}`, { withCredentials: true }).then(r => setData(r.data)).catch(() => setData({ sessions: [], total: 0 }));
  }, []);
  useEffect(() => { load(limit); }, [limit, load]);

  if (!data) return <LoadingSpinner />;
  const { sessions, total } = data;
  return (
    <div data-testid="activity-sessions">
      <p className={`text-xs mb-3 ${txtSub(light)}`}>{total.toLocaleString('fr-FR')} session{total > 1 ? 's' : ''} enregistrée{total > 1 ? 's' : ''} (30 derniers jours) — cliquez sur une ligne pour voir le parcours détaillé.</p>
      {sessions.length === 0 && <p className={`text-sm py-8 text-center ${txtSub(light)}`}>Aucune session pour l'instant — les données apparaissent dès que vos utilisateurs naviguent sur la plateforme.</p>}
      <div className="space-y-2">
        {sessions.map((s, i) => {
          const isOpen = open === s.session_id;
          return (
            <div key={s.session_id} className={`${cardCls(light)} overflow-hidden`} data-testid={`session-row-${i}`}>
              <button onClick={() => setOpen(isOpen ? null : s.session_id)} className="w-full flex items-center gap-3 p-3.5 text-left">
                <DeviceChip kind={s.kind} device={s.device} light={light} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold truncate ${txtMain(light)}`}>
                    <span role="button" tabIndex={0} data-testid={`session-user-btn-${i}`}
                      onClick={(e) => { e.stopPropagation(); if (s.user_id) setUserId(s.user_id); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); if (s.user_id) setUserId(s.user_id); } }}
                      className="hover:text-[#E9641A] hover:underline transition-colors cursor-pointer">{s.user_name || s.user_email}</span>
                    {s.profile_name ? ` · ${s.profile_name}` : ''}
                  </p>
                  <p className={`text-[11px] truncate ${txtSub(light)}`}>{fmtDT(s.started_at)} · durée {fmtDur(s.duration_s)} · {s.nav_count || 0} action{(s.nav_count || 0) > 1 ? 's' : ''}{(s.watched || []).length > 0 ? ` · ${s.watched.length} contenu${s.watched.length > 1 ? 's' : ''} regardé${s.watched.length > 1 ? 's' : ''}` : ''}</p>
                </div>
                {s.first_play_delay_s !== null && s.first_play_delay_s !== undefined && (
                  <span className={`hidden md:inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md ${light ? 'bg-gray-100 text-gray-600' : 'bg-white/8 text-white/60'}`}>
                    <Clock size={11} className="text-[#E9641A]" /> 1re lecture après {fmtDur(s.first_play_delay_s)}
                  </span>
                )}
                {s.first_play_at && (
                  <span className={`hidden md:inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md ${s.info_before_play ? 'bg-emerald-500/10 text-emerald-500' : light ? 'bg-gray-100 text-gray-500' : 'bg-white/8 text-white/50'}`}>
                    {s.info_before_play ? <CheckCircle2 size={11} /> : <XCircle size={11} />} Fiche avant lecture
                  </span>
                )}
                {(s.watch_seconds || 0) > 0 && <span className="text-[11px] font-bold text-[#E9641A] whitespace-nowrap">{fmtDur(s.watch_seconds)} de visionnage</span>}
                {isOpen ? <ChevronUp size={15} className={txtSub(light)} /> : <ChevronDown size={15} className={txtSub(light)} />}
              </button>
              {isOpen && (
                <div className={`px-4 pb-4 border-t ${light ? 'border-gray-100' : 'border-white/5'}`} data-testid="session-detail">
                  {(s.watched || []).length > 0 && (
                    <div className="mt-3 mb-3 flex flex-wrap gap-1.5">
                      {s.watched.map((t, j) => <span key={j} className="text-[11px] font-semibold px-2 py-1 rounded-md bg-[#E9641A]/10 text-[#E9641A]"><Play size={9} className="inline mr-1 -mt-0.5" />{t}</span>)}
                    </div>
                  )}
                  <div className="mt-3 space-y-0">
                    {(s.events || []).map((e, j) => {
                      const Icon = EV_ICON[e.type] || MousePointer;
                      return (
                        <div key={j} className="flex items-start gap-2.5 relative pb-2.5">
                          {j < s.events.length - 1 && <span className={`absolute left-[9px] top-5 bottom-0 w-px ${light ? 'bg-gray-200' : 'bg-white/10'}`} />}
                          <span className={`w-[19px] h-[19px] rounded-full flex items-center justify-center flex-none mt-0.5 ${e.type === 'play' ? 'bg-[#E9641A]/15' : light ? 'bg-gray-100' : 'bg-white/8'}`}>
                            <Icon size={10} className={e.type === 'play' ? 'text-[#E9641A]' : txtSub(light)} />
                          </span>
                          <p className={`text-[11px] leading-5 ${txtSub(light)}`}>
                            <span className={`font-mono ${light ? 'text-gray-400' : 'text-white/30'}`}>{fmtTime(e.t)}</span>
                            {' '}<span className={`font-semibold ${txtMain(light)}`}>{EV_LABEL[e.type]}</span>
                            {e.title ? <> — <span className="text-[#E9641A] font-semibold">{e.title}</span></> : e.page ? <> — {e.page}</> : null}
                          </p>
                        </div>
                      );
                    })}
                    {(s.events || []).length === 0 && <p className={`text-[11px] ${txtSub(light)}`}>Aucun événement détaillé pour cette session.</p>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {sessions.length < total && (
        <button data-testid="sessions-load-more" onClick={() => setLimit(l => l + 40)}
          className={`mt-4 px-4 py-2 rounded-lg text-xs font-bold ${light ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-white/8 text-white hover:bg-white/15'}`}>
          Charger plus ({sessions.length}/{total})
        </button>
      )}
      {userId && <UserActivityModal userId={userId} light={light} onClose={() => setUserId(null)} />}
    </div>
  );
}

/* ─── AUDIENCE ─── */
export function ActivityAudience({ theme }) {
  const light = theme === 'light';
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/admin/activity/audience?days=${days}`, { withCredentials: true }).then(r => setData(r.data)).catch(() => {});
  }, [days]);

  if (!data) return <LoadingSpinner />;
  const maxDay = Math.max(1, ...data.by_day.map(d => Math.max(d.sessions, d.prev_sessions || 0)));
  const maxHour = Math.max(1, ...data.by_hour.map(h => h.sessions));
  const totalKind = Object.values(data.by_kind).reduce((a, b) => a + b, 0) || 1;
  const totWatch = data.by_day.reduce((a, d) => a + d.watch_seconds, 0);
  const totSess = data.by_day.reduce((a, d) => a + d.sessions, 0);
  const cmp = data.compare || {};
  const periodLbl = days === 7 ? 'semaine précédente' : `${days} jours précédents`;

  const TrendBadge = ({ delta }) => {
    if (delta == null) return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${light ? 'bg-gray-100 text-gray-400' : 'bg-white/8 text-white/40'}`}>—</span>;
    const up = delta > 0;
    const flat = delta === 0;
    return (
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${flat ? (light ? 'bg-gray-100 text-gray-400' : 'bg-white/8 text-white/40') : up ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-400'}`}>
        {flat ? '=' : up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}{flat ? '' : ` ${up ? '+' : ''}${delta}%`}
      </span>
    );
  };

  return (
    <div data-testid="activity-audience">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className={`text-xs ${txtSub(light)}`}>{totSess.toLocaleString('fr-FR')} connexions · {fmtDur(totWatch)} de visionnage cumulé (heure de Paris)</p>
        <div className={`flex rounded-lg p-0.5 ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
          {[[7, '7 j'], [14, '14 j'], [30, '30 j']].map(([v, l]) => (
            <button key={v} data-testid={`audience-days-${v}`} onClick={() => setDays(v)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md ${days === v ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500' : 'text-white/40'}`}>{l}</button>
          ))}
        </div>
      </div>
      {/* Comparaison avec la période précédente */}
      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="audience-compare">
        {[['Connexions', cmp.sessions, v => v.toLocaleString('fr-FR')],
          ['Utilisateurs uniques', cmp.users, v => v.toLocaleString('fr-FR')],
          ['Temps visionné', cmp.watch_seconds, fmtDur]].map(([lbl, c, fmt], i) => (
          <div key={i} className={`${cardCls(light)} p-4`} data-testid={`audience-trend-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <p className={`text-lg font-bold ${txtMain(light)}`}>{c ? fmt(c.cur) : '—'}</p>
              <TrendBadge delta={c?.delta_pct} />
            </div>
            <p className={`text-[10px] uppercase font-bold mt-0.5 ${txtSub(light)}`}>{lbl}</p>
            <p className={`text-[10px] mt-0.5 ${txtSub(light)}`}>vs {periodLbl} : {c ? fmt(c.prev) : '—'}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className={`${cardCls(light)} p-5`}>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h3 className={`text-sm font-semibold flex items-center gap-2 ${txtMain(light)}`}><Users size={15} className="text-[#E9641A]" /> Connexions par jour</h3>
            <span className={`text-[10px] flex items-center gap-3 ${txtSub(light)}`}>
              <span className="flex items-center gap-1"><span className={`w-2 h-2 rounded-sm inline-block ${light ? 'bg-gray-300' : 'bg-white/20'}`} /> {periodLbl}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#E9641A]/70 inline-block" /> période actuelle</span>
            </span>
          </div>
          <div className="flex items-end gap-1.5 h-36">
            {data.by_day.map((d, i) => (
              <div key={i} className="flex-1 h-full flex flex-col justify-end items-center gap-1" title={`${d.date} · ${d.sessions} sessions (vs ${d.prev_sessions || 0} le ${d.prev_date}) · ${d.users} utilisateurs · ${fmtDur(d.watch_seconds)} regardées`}>
                <span className={`text-[9px] leading-none ${txtSub(light)}`}>{d.sessions || ''}</span>
                <div className="w-full flex items-end gap-[2px]" style={{ height: '78%' }}>
                  <div className={`flex-1 rounded-t ${light ? 'bg-gray-300' : 'bg-white/20'}`} style={{ height: `${Math.max(2, ((d.prev_sessions || 0) / maxDay) * 100)}%` }} />
                  <div className="flex-1 bg-[#E9641A]/70 rounded-t" style={{ height: `${Math.max(2, (d.sessions / maxDay) * 100)}%` }} />
                </div>
                <span className={`text-[9px] ${txtSub(light)}`}>{d.date.slice(8)}/{d.date.slice(5, 7)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={`${cardCls(light)} p-5`}>
          <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${txtMain(light)}`}><Clock size={15} className="text-[#E9641A]" /> Connexions par heure (pics d'audience)</h3>
          <div className="flex items-end gap-[3px] h-36">
            {data.by_hour.map((h) => (
              <div key={h.hour} className="flex-1 h-full flex flex-col justify-end items-center gap-0.5" title={`${h.hour}h — ${h.sessions} connexion${h.sessions > 1 ? 's' : ''}`}>
                <span className={`text-[8px] leading-none ${h.sessions > 0 ? 'font-bold text-teal-500' : txtSub(light)}`}>{h.sessions > 0 ? h.sessions : ''}</span>
                <div className="w-full bg-teal-500/70 rounded-t" style={{ height: `${Math.max(2, (h.sessions / maxHour) * 78)}%` }} />
                {h.hour % 3 === 0 && <span className={`text-[8px] ${txtSub(light)}`}>{h.hour}h</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={`${cardCls(light)} p-5 max-w-xl`}>
        <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${txtMain(light)}`}><Monitor size={15} className="text-[#E9641A]" /> Répartition par appareil</h3>
        <div className="space-y-2.5">
          {['web', 'tv', 'mobile', 'tablet'].map(k => {
            const n = data.by_kind[k] || 0;
            const Icon = KIND_ICON[k];
            return (
              <div key={k} className="flex items-center gap-3">
                <span className={`text-xs w-24 flex items-center gap-1.5 ${txtSub(light)}`}><Icon size={12} className="text-[#E9641A]" />{KIND_LABEL[k]}</span>
                <div className={`flex-1 h-2 rounded-full ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
                  <div className="h-full bg-[#E9641A] rounded-full" style={{ width: `${(n / totalKind) * 100}%` }} />
                </div>
                <span className={`text-xs w-14 text-right ${txtSub(light)}`}>{n} ({Math.round((n / totalKind) * 100)}%)</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── CONNEXIONS RÉCENTES ─── */
const CONN_PERIODS = [
  ['hour', 'Dernière heure'], ['day', "Aujourd'hui (24h)"], ['3days', '3 derniers jours'],
  ['week', 'Semaine'], ['month', 'Mois'],
];

export function ActivityConnections({ theme }) {
  const light = theme === 'light';
  const [period, setPeriod] = useState('day');
  const [data, setData] = useState(null);
  const [userId, setUserId] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/admin/activity/recent-connections?period=${period}`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => setData({ connections: [], total: 0 }));
  }, [period]);

  return (
    <div data-testid="activity-connections">
      {userId && <UserActivityModal userId={userId} light={light} onClose={() => setUserId(null)} />}
      <InactiveAlert light={light} onOpenUser={setUserId} />
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className={`flex gap-1 rounded-lg p-1 w-fit flex-wrap ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
          {CONN_PERIODS.map(([id, lbl]) => (
            <button key={id} data-testid={`conn-period-${id}`} onClick={() => setPeriod(id)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                period === id ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500 hover:text-gray-900' : 'text-white/50 hover:text-white'}`}>
              {lbl}
            </button>
          ))}
        </div>
        {data && <span className={`text-xs ${txtSub(light)}`} data-testid="conn-total">{data.total} abonné{data.total > 1 ? 's' : ''} connecté{data.total > 1 ? 's' : ''}</span>}
      </div>
      {!data ? <LoadingSpinner /> : data.connections.length === 0 ? (
        <div className={`${cardCls(light)} p-10 text-center`}>
          <Wifi size={28} className={`mx-auto mb-3 ${light ? 'text-gray-300' : 'text-white/15'}`} />
          <p className={`text-sm ${txtSub(light)}`}>Aucune connexion sur cette période</p>
        </div>
      ) : (
        <div className={`${cardCls(light)} overflow-hidden`}>
          <div className={`hidden md:grid grid-cols-[1.6fr_1fr_1fr_0.6fr_0.8fr_1.4fr_0.3fr] gap-3 px-5 py-3 text-[10px] font-bold uppercase tracking-wider border-b ${light ? 'text-gray-400 border-gray-100' : 'text-white/30 border-white/5'}`}>
            <span>Abonné</span><span>Dernière connexion</span><span>Appareils</span><span>Sessions</span><span>Visionnage</span><span>A regardé</span><span />
          </div>
          {data.connections.map(c => (
            <div key={c.user_id} className={`border-b last:border-0 ${light ? 'border-gray-100' : 'border-white/5'}`}>
              <div data-testid={`conn-row-${c.user_id}`} onClick={() => setExpanded(expanded === c.user_id ? null : c.user_id)}
                className={`grid grid-cols-1 md:grid-cols-[1.6fr_1fr_1fr_0.6fr_0.8fr_1.4fr_0.3fr] gap-2 md:gap-3 px-5 py-3.5 items-center cursor-pointer transition-colors ${light ? 'hover:bg-gray-50' : 'hover:bg-white/[0.03]'}`}>
                <div className="min-w-0">
                  <p className={`text-xs font-semibold truncate ${txtMain(light)}`}>{c.name || c.email}</p>
                  <p className={`text-[10px] truncate ${txtSub(light)}`}>{c.email}{c.plan ? ` · ${c.plan}` : ''}</p>
                </div>
                <div>
                  <p className={`text-xs ${txtMain(light)}`}>{fmtDT(c.last_seen)}</p>
                  <p className={`text-[10px] ${txtSub(light)}`}>il y a {since(c.last_seen)}</p>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(c.devices || []).slice(0, 2).map(d => <span key={d} className={`px-1.5 py-0.5 rounded text-[10px] ${light ? 'bg-gray-100 text-gray-600' : 'bg-white/8 text-white/60'}`}>{d}</span>)}
                  {(c.devices || []).length > 2 && <span className={`text-[10px] ${txtSub(light)}`}>+{c.devices.length - 2}</span>}
                </div>
                <span className={`text-xs ${txtMain(light)}`}>{c.sessions}</span>
                <span className={`text-xs ${txtMain(light)}`}>{fmtDur(c.watch_seconds)}</span>
                <div className="flex flex-wrap gap-1 min-w-0">
                  {(c.watched || []).slice(0, 2).map(t => <span key={t} className="px-1.5 py-0.5 rounded text-[10px] bg-[#E9641A]/15 text-[#E9641A] truncate max-w-[130px]">{t}</span>)}
                  {(c.watched || []).length > 2 && <span className={`text-[10px] ${txtSub(light)}`}>+{c.watched.length - 2}</span>}
                  {(c.watched || []).length === 0 && <span className={`text-[10px] ${txtSub(light)}`}>—</span>}
                </div>
                {expanded === c.user_id ? <ChevronUp size={14} className={txtSub(light)} /> : <ChevronDown size={14} className={txtSub(light)} />}
              </div>
              {expanded === c.user_id && (
                <div className={`px-5 pb-4 pt-1 text-xs space-y-2 ${light ? 'bg-gray-50/50' : 'bg-white/[0.02]'}`} data-testid={`conn-detail-${c.user_id}`}>
                  <div className="flex flex-wrap gap-x-6 gap-y-1.5">
                    <span className={txtSub(light)}>Première connexion : <b className={txtMain(light)}>{fmtDT(c.first_seen)}</b></span>
                    <span className={txtSub(light)}>Navigation : <b className={txtMain(light)}>{c.nav_count} action{c.nav_count > 1 ? 's' : ''}</b></span>
                    {(c.profiles || []).length > 0 && <span className={txtSub(light)}>Profils : <b className={txtMain(light)}>{c.profiles.join(', ')}</b></span>}
                    {(c.cities || []).length > 0 && <span className={`inline-flex items-center gap-1 ${txtSub(light)}`}><MapPin size={11} />{c.cities.join(' · ')}</span>}
                  </div>
                  {(c.watched || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 items-center">
                      <span className={`inline-flex items-center gap-1 ${txtSub(light)}`}><Play size={11} /> A regardé :</span>
                      {c.watched.map(t => <span key={t} className="px-1.5 py-0.5 rounded text-[10px] bg-[#E9641A]/15 text-[#E9641A]">{t}</span>)}
                    </div>
                  )}
                  {(c.viewed_pages || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 items-center">
                      <span className={`inline-flex items-center gap-1 ${txtSub(light)}`}><Eye size={11} /> Fiches consultées :</span>
                      {c.viewed_pages.map(t => <span key={t} className={`px-1.5 py-0.5 rounded text-[10px] ${light ? 'bg-gray-100 text-gray-600' : 'bg-white/8 text-white/60'}`}>{t}</span>)}
                    </div>
                  )}
                  <button data-testid={`conn-open-user-${c.user_id}`} onClick={(e) => { e.stopPropagation(); setUserId(c.user_id); }}
                    className="text-[11px] font-semibold text-[#E9641A] hover:underline">Voir la fiche complète →</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── ALERTE INACTIVITÉ ─── */
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };

function InactiveAlert({ light, onOpenUser }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState({});
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/subscribers/inactive?days=${days}`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => setData({ subscribers: [], total: 0 }));
  }, [days]);
  useEffect(() => { setResult(null); load(); }, [load]);

  const remind = async (emails) => {
    setSending(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/subscribers/inactive/remind`, { days, emails }, { withCredentials: true });
      setResult(r);
      if (emails) setSentTo(p => ({ ...p, ...Object.fromEntries(emails.map(e => [e, true])) }));
      load();
    } catch { setResult({ error: true }); }
    setSending(false);
  };

  if (!data || (data.total === 0 && days === 30)) return null;
  return (
    <div className={`mb-4 rounded-xl border ${light ? 'bg-amber-50 border-amber-200' : 'bg-amber-500/[0.07] border-amber-500/25'}`} data-testid="inactive-alert">
      <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-3 cursor-pointer" onClick={() => setOpen(o => !o)} data-testid="inactive-alert-header">
        <div className="flex items-center gap-2.5">
          <AlertTriangle size={17} className="text-amber-500 shrink-0" />
          <p className={`text-sm font-semibold ${light ? 'text-amber-800' : 'text-amber-300'}`} data-testid="inactive-alert-count">
            {data.total === 0 ? `Aucun abonné inactif depuis plus de ${days} jours` : `${data.total} abonné${data.total > 1 ? 's' : ''} inactif${data.total > 1 ? 's' : ''} depuis plus de ${days} jours`}
          </p>
        </div>
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <select value={days} onChange={e => setDays(Number(e.target.value))} data-testid="inactive-days-select"
            className={`text-xs rounded-md px-2 py-1.5 border outline-none ${light ? 'bg-white border-amber-200 text-amber-800' : 'bg-[#1a0e2e] border-amber-500/25 text-amber-300'}`}>
            <option value={30}>30 jours</option><option value={60}>60 jours</option><option value={90}>90 jours</option>
          </select>
          {data.total > 0 && <button disabled={sending} onClick={() => remind(null)} data-testid="inactive-remind-all-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-amber-500 text-black hover:bg-amber-400 transition-colors disabled:opacity-50">
            <Send size={12} />{sending ? 'Envoi…' : `Relancer tous (${data.total})`}
          </button>}
          <button onClick={() => setOpen(o => !o)} className={light ? 'text-amber-700' : 'text-amber-300'}>
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>
      {result && (
        <p className={`px-4 pb-2 text-xs ${light ? 'text-amber-700' : 'text-amber-300/80'}`} data-testid="inactive-remind-result">
          {result.error ? "Erreur lors de l'envoi" : `${result.sent} email${result.sent > 1 ? 's' : ''} envoyé${result.sent > 1 ? 's' : ''}${result.skipped_cooldown ? ` · ${result.skipped_cooldown} ignoré(s) — déjà relancé(s) il y a moins de 7 jours` : ''}`}
        </p>
      )}
      {open && (
        <div className={`border-t ${light ? 'border-amber-200' : 'border-amber-500/20'}`}>
          {data.subscribers.map(s => (
            <div key={s.user_id} data-testid={`inactive-row-${s.user_id}`}
              className={`flex items-center justify-between gap-3 px-4 py-2.5 border-b last:border-0 ${light ? 'border-amber-100' : 'border-amber-500/10'}`}>
              <div className="min-w-0 cursor-pointer" onClick={() => onOpenUser(s.user_id)}>
                <p className={`text-xs font-semibold truncate ${light ? 'text-gray-900' : 'text-white'}`}>{s.name || s.email}</p>
                <p className={`text-[10px] truncate ${light ? 'text-gray-500' : 'text-white/45'}`}>{s.email}{s.plan ? ` · ${s.plan}` : ''}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <p className={`text-xs font-semibold ${light ? 'text-amber-700' : 'text-amber-300'}`}>{s.days_inactive} jours</p>
                  <p className={`text-[10px] ${light ? 'text-gray-500' : 'text-white/40'}`}>
                    {s.last_login_at ? `Vu le ${fmtDate(s.last_login_at)}` : 'Jamais connecté'}{s.last_reminded_at ? ` · relancé le ${fmtDate(s.last_reminded_at)}` : ''}
                  </p>
                </div>
                <button disabled={sending || sentTo[s.email]} onClick={() => remind([s.email])} data-testid={`inactive-remind-${s.user_id}`}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition-colors disabled:opacity-60 ${sentTo[s.email] ? 'bg-green-500/15 text-green-500' : light ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25'}`}>
                  {sentTo[s.email] ? <><CheckCircle2 size={12} />Envoyé</> : <><Send size={11} />Relancer</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
